import React, { ReactNode, useEffect, useState } from 'react';

import cn from 'classnames';
import { Link } from 'react-router-dom';

import Skeleton from 'components/Skeleton';
import Spinner from 'components/Spinner';
import ArrowRightIcon from 'design/Icons/ArrowRightIcon';
import { useAppSelector } from 'hooks/useStore';
import { selectShowAuthNews } from 'store/modules/ui';
import { apiGet } from 'utils/request';
import {
  BRAND,
  CACHE_NETWORKS,
  DOMAIN_NAME,
  isCacheFly,
  logoFullDark,
} from 'utils/whiteLabeling';

import styles from './auth.module.scss';

type Post = {
  title: string;
  excerpt: string;
  link: string;
  image: string;
};

interface AuthLayoutProps {
  children: ReactNode;
}

const AuthLayout = ({ children }: AuthLayoutProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [post, setPost] = useState<Post>({} as Post);
  const [hasError, setHasError] = useState(false);
  const showAuthNews = useAppSelector(selectShowAuthNews);

  const fetchNews = async () => {
    setIsLoading(true);
    try {
      const res = await apiGet('/auth/promo');
      if (!res || res.length === 0 || !res[0].title || !res[0].excerpt || !res[0].link) {
        setHasError(true);
        return;
      }
      setPost({
        title: res[0].title.rendered,
        excerpt: res[0].excerpt.rendered,
        link: res[0].link,
        image: res[0].featuredImage,
      });
    } catch (e) {
      console.log(e);
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.body.classList.add('user-auth');
    document.body.classList.add('new-portal');
    fetchNews();
  }, []);

  let showNews = !hasError;
  const newsHidden = !isCacheFly || !showAuthNews || !showNews;

  if (isLoading) {
    return <Spinner main />;
  }

  return (
    <div className="user-auth-wrapper">
      <div className="user-auth-main">
        <div className={cn('user-auth-container', newsHidden && 'user-auth-form-only')}>
          <div
            className={cn('user-auth-content', !showAuthNews && 'user-auth-content-wide')}
          >
            <Link to="/" className="logo">
              <img src={logoFullDark()} alt="logo" width="200" height="93" />
            </Link>
            {children}
          </div>

          {!newsHidden && (
            <div
              className={styles.news}
              style={{
                backgroundImage: `url(${post.image})`,
              }}
            >
              {isLoading ? (
                <Skeleton
                  height="100%"
                  duration={2}
                  borderRadius={12}
                  className={styles.skeleton}
                />
              ) : (
                <div className={styles.overlay}>
                  <div className={styles.info}>
                    <h3 dangerouslySetInnerHTML={{ __html: post.title }} />
                    <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                    <a
                      href={post.link}
                      className="p-link-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Learn More <ArrowRightIcon />
                    </a>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="user-auth-footer">
        <ul>
          {isCacheFly && (
            <li>
              <a
                href={`${DOMAIN_NAME}/terms-conditions`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Service
              </a>
            </li>
          )}
          {isCacheFly && (
            <li>
              <a
                href={`${DOMAIN_NAME}/privacy-policy`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy
              </a>
            </li>
          )}
          {isCacheFly && (
            <li>
              <a
                href="https://www.cacheflystatus.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                Service Status
              </a>
            </li>
          )}
          <li>
            <a href={DOMAIN_NAME} target="_blank" rel="noopener noreferrer">
              © {new Date().getFullYear()} {isCacheFly ? CACHE_NETWORKS : BRAND}
            </a>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default AuthLayout;
